import React from 'react';
import useSWR from 'swr';
import { AlertCircle, CheckCircle2, Circle, Clock } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import { listOpsTasks, type OpsTaskListItem, type OpsTaskStatus } from '@/api/opsTasks';

const CARD_TITLE = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

function statusIcon(status: OpsTaskStatus): { Icon: React.ComponentType<{ className?: string }>; color: string } {
  switch (status) {
    case 'overdue':
      return { Icon: AlertCircle, color: 'text-destructive' };
    case 'pre_deadline':
      return { Icon: Clock, color: 'text-warning' };
    case 'done':
      return { Icon: CheckCircle2, color: 'text-success' };
    default:
      return { Icon: Circle, color: 'text-muted-foreground' };
  }
}

function fmtTime(iso?: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function ProgressRing({ done, total }: { done: number; total: number }) {
  const r = 14;
  const c = 2 * Math.PI * r;
  const frac = total > 0 ? done / total : 0;
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" className="shrink-0 -rotate-90">
      <circle cx="18" cy="18" r={r} fill="none" stroke="var(--color-muted)" strokeWidth="4" />
      <circle
        cx="18"
        cy="18"
        r={r}
        fill="none"
        stroke="var(--color-success, #22c55e)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - frac)}
      />
    </svg>
  );
}

// Active (overdue → due-soon → upcoming) first, done last.
const ORDER: Record<OpsTaskStatus, number> = {
  overdue: 0,
  pre_deadline: 1,
  pending: 2,
  created: 2,
  done: 3,
};

/**
 * The day's checklist run, merged into one report card: a progress ring +
 * "Готово X из Y" and the full task list (done shown, not collapsed). Read-only —
 * no "сделать фото" widget. Hidden when the day had no tasks.
 */
export function DayTasksCard({ zoneId, day }: { zoneId: string; day: string }) {
  const { t } = useT();
  const { data } = useSWR(
    zoneId ? ['report:day-tasks', zoneId, day] : null,
    () => listOpsTasks({ zoneId, date: day }).then((r) => r.data),
  );

  const tasks: OpsTaskListItem[] = data?.results ?? [];
  if (tasks.length === 0) return null;

  const total = tasks.length;
  const done = tasks.filter((tk) => tk.status === 'done').length;
  const sorted = [...tasks].sort(
    (a, b) => ORDER[a.status] - ORDER[b.status] || ((a.due_at ?? '') < (b.due_at ?? '') ? -1 : 1),
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-3 pt-4 pb-2">
        <ProgressRing done={done} total={total} />
        <CardTitle className={CARD_TITLE}>{t('summaries.tasksDone', { done, total })}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border">
          {sorted.map((tk) => {
            const { Icon, color } = statusIcon(tk.status);
            const isDone = tk.status === 'done';
            const time = isDone ? fmtTime(tk.done_at) : fmtTime(tk.due_at);
            return (
              <li key={tk.id} className="flex items-center gap-2 py-1.5 text-sm">
                <Icon className={cn('size-4 shrink-0', color)} />
                <span className={cn('min-w-0 flex-1 truncate', isDone && 'text-muted-foreground line-through')}>
                  {tk.title}
                </span>
                {time && (
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{time}</span>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
